import React, { useEffect, useMemo, useRef, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import Layout from '../components/Layout';
import SearchBar from '../components/SearchBar';
import { fetchPackages, deletePackage } from '../api/Packages';
import { TravelPackage } from '../types/TravelPackage';
import '../css/PackagesPage.css';
import * as FiIcons from 'react-icons/fi';

const FiRefreshCw = FiIcons.FiRefreshCw as unknown as React.FC;
const FiPlus = FiIcons.FiPlus as unknown as React.FC;
const FiCopy = FiIcons.FiCopy as unknown as React.FC;
const FiEye = FiIcons.FiEye as unknown as React.FC;
const FiEdit2 = FiIcons.FiEdit2 as unknown as React.FC;
const FiTrash2 = FiIcons.FiTrash2 as unknown as React.FC;

type SortKey = 'title' | 'slug' | 'duration' | 'cities' | 'price' | 'published' | 'updated';

const PackagesPage: React.FC = () => {
  const navigate = useNavigate();
  const hasFetched = useRef(false);

  const [packages, setPackages] = useState<TravelPackage[]>([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [search, setSearch] = useState('');
  const [onlyPublished, setOnlyPublished] = useState(false);
  const [sortKey, setSortKey] = useState<SortKey>('updated');
  const [sortDir, setSortDir] = useState<'asc' | 'desc'>('desc');

  const load = async () => {
    try {
      setLoading(true);
      setError('');
      const data = await fetchPackages();
      setPackages(Array.isArray(data) ? data : []);
    } catch (e: any) {
      setError(e?.message || 'Failed to load packages');
      setPackages([]);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    if (hasFetched.current) return;
    hasFetched.current = true;
    load();
  }, []);

  const filtered = useMemo(() => {
    const q = search.trim().toLowerCase();
    const base = packages.filter(p =>
      (!onlyPublished || !!p.is_published) &&
      (
        p.title?.toLowerCase().includes(q) ||
        p.slug?.toLowerCase().includes(q) ||
        (p.cities || []).join(', ').toLowerCase().includes(q)
      )
    );

    const withDerived = base.map(p => ({
      ...p,
      __duration: `${p.duration_days || 0}d/${p.duration_nights || 0}n`,
      __cities: (p.cities || []).join(', '),
      __price: p.price?.base ?? 0,
      __updated: p.updated_at || p.created_at || '',
    }));

    const dir = sortDir === 'asc' ? 1 : -1;
    return withDerived.slice().sort((a, b) => {
      switch (sortKey) {
        case 'title':
          return a.title.localeCompare(b.title) * dir;
        case 'slug':
          return (a.slug || '').localeCompare(b.slug || '') * dir;
        case 'duration': {
          const an = a.duration_nights || 0; const bn = b.duration_nights || 0;
          if (an === bn) return ((a.duration_days || 0) - (b.duration_days || 0)) * dir;
          return (an - bn) * dir;
        }
        case 'cities':
          return a.__cities.localeCompare(b.__cities) * dir;
        case 'price':
          return ((a.__price || 0) - (b.__price || 0)) * dir;
        case 'published':
          return ((a.is_published ? 1 : 0) - (b.is_published ? 1 : 0)) * dir;
        case 'updated':
        default: {
          const at = a.__updated ? new Date(a.__updated).getTime() : 0;
          const bt = b.__updated ? new Date(b.__updated).getTime() : 0;
          return (at - bt) * dir;
        }
      }
    });
  }, [packages, search, sortKey, sortDir, onlyPublished]);

  const toggleSort = (key: SortKey) => {
    if (sortKey === key) {
      setSortDir(prev => (prev === 'asc' ? 'desc' : 'asc'));
    } else {
      setSortKey(key);
      setSortDir('asc');
    }
  };

  const onModifyCopy = (p: TravelPackage) => {
    const copy = { ...p };
    navigate('/package/builder', { state: { packageToCopy: copy } });
  };

  const onView = (p: TravelPackage) => {
    if (!p.slug) return;
    const url = `https://www.otaprime.com/packages/${encodeURIComponent(p.slug)}`;
    window.open(url, '_blank', 'noopener');
  };

  const onEdit = (p: TravelPackage) => {
    navigate('/package/builder', { state: { packageToEdit: p } });
  };

  const onDelete = async (p: TravelPackage) => {
    if (!p.id) return;
    const ok = window.confirm(`Delete package "${p.title}"?`);
    if (!ok) return;
    try {
      await deletePackage(String(p.id));
      setPackages(prev => prev.filter(x => x.id !== p.id));
    } catch (e: any) {
      alert(e?.message || 'Failed to delete package');
    }
  };

  return (
    <Layout>
      <div className="packages-page">
        <div className="packages-header">
          <h2>Packages</h2>
          <div className="packages-actions">
            <button className="btn" onClick={() => navigate('/package/builder')}>
              <FiPlus /> New Package
            </button>
            <button className="btn" onClick={load} disabled={loading}>
              <FiRefreshCw /> {loading ? 'Loading…' : 'Refresh'}
            </button>
          </div>
        </div>

        <div className="packages-filters">
          <SearchBar searchTerm={search} onSearchChange={setSearch} placeholder="Search by title, slug, city" />
          <label className="checkbox">
            <input type="checkbox" checked={onlyPublished} onChange={e => setOnlyPublished(e.target.checked)} />
            <span>Only published</span>
          </label>
        </div>

        {error && <div className="packages-error">{error}</div>}

        <div className="packages-table-wrapper">
          <table className="packages-table">
            <thead>
              <tr>
                <th onClick={() => toggleSort('title')}>
                  Title {sortKey === 'title' && (sortDir === 'asc' ? '▲' : '▼')}
                </th>
                <th onClick={() => toggleSort('slug')}>
                  Slug {sortKey === 'slug' && (sortDir === 'asc' ? '▲' : '▼')}
                </th>
                <th onClick={() => toggleSort('duration')}>
                  Duration {sortKey === 'duration' && (sortDir === 'asc' ? '▲' : '▼')}
                </th>
                <th onClick={() => toggleSort('cities')}>
                  Cities {sortKey === 'cities' && (sortDir === 'asc' ? '▲' : '▼')}
                </th>
                <th onClick={() => toggleSort('price')}>
                  Price {sortKey === 'price' && (sortDir === 'asc' ? '▲' : '▼')}
                </th>
                <th onClick={() => toggleSort('published')}>
                  Published {sortKey === 'published' && (sortDir === 'asc' ? '▲' : '▼')}
                </th>
                <th onClick={() => toggleSort('updated')}>
                  Updated {sortKey === 'updated' && (sortDir === 'asc' ? '▲' : '▼')}
                </th>
                <th>Actions</th>
              </tr>
            </thead>
            <tbody>
              {filtered.map(p => (
                <tr key={p.id || p.slug}>
                  <td className="col-title">{p.title}</td>
                  <td className="col-slug">{p.slug}</td>
                  <td className="col-duration">{(p.duration_days || 0)}d/{(p.duration_nights || 0)}n</td>
                  <td className="col-cities">{(p.cities || []).join(', ')}</td>
                  <td className="col-price">{p.price?.currency || 'INR'} {p.price?.base?.toLocaleString?.('en-IN') ?? p.price?.base ?? '-'}</td>
                  <td className="col-published">
                    <span className={`badge ${p.is_published ? 'on' : 'off'}`}>{p.is_published ? 'Yes' : 'No'}</span>
                  </td>
                  <td className="col-updated">{(p.updated_at || p.created_at || '').replace('T', ' ').replace('Z', '')}</td>
                  <td className="col-actions">
                    <div className="action-buttons">
                      {(() => { const canView = !!p.is_published && !!p.slug; return (
                        <button className="btn-secondary icon-only" title={canView ? 'View' : 'View (published only)'} onClick={() => canView && onView(p)} disabled={!canView}>
                          <FiEye />
                        </button>
                      ); })()}
                      <button className="btn-secondary icon-only" title="Edit" onClick={() => onEdit(p)}>
                        <FiEdit2 />
                      </button>
                      <button className="btn-secondary icon-only" title="Copy" onClick={() => onModifyCopy(p)}>
                        <FiCopy />
                      </button>
                      <button className="btn-secondary icon-only danger" title="Delete" onClick={() => onDelete(p)}>
                        <FiTrash2 />
                      </button>
                    </div>
                  </td>
                </tr>
              ))}
              {filtered.length === 0 && !loading && (
                <tr>
                  <td colSpan={8} className="empty-state">No packages found</td>
                </tr>
              )}
            </tbody>
          </table>
        </div>
      </div>
    </Layout>
  );
};

export default PackagesPage;
